/**
 * SECURITY NOTE: Token Validation Hook
 * 
 * This hook validates user sessions and provides authentication status (isAdmin, isValid, etc.)
 * for CLIENT-SIDE UI/UX purposes only.
 * 
 * IMPORTANT: The isAdmin flag returned by this hook is for UI rendering only.
 * It should NEVER be trusted for actual authorization decisions.
 * 
 * Server-side authorization is enforced by:
 * 1. Row Level Security (RLS) policies on all database tables (423 policies)
 * 2. Server-side admin verification via verifyAdminRole() in edge functions
 * 3. The validate-session-cookie edge function verifies tokens server-side
 * 4. Admin operations query the user_roles table directly for verification
 * 
 * To verify admin access in edge functions, use:
 *   import { verifyAdminRole } from '../_shared/admin-verification.ts';
 *   const result = await verifyAdminRole(supabaseAdmin, profileId);
 *   if (!result.isAdmin) return createUnauthorizedResponse(corsHeaders);
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { getAuthFromCookie, clearAuthCookie } from '@/utils/cookies';
import { useSessionTimeout } from './useSessionTimeout';
import { authLogger } from '@/utils/authLogger';

export interface TokenValidationResult {
  isValid: boolean;
  userEmail: string | null;
  userName: string | null;
  paymentStatus: string | null;
  expiresAt: Date | null;
  usesRemaining: number | null;
  failureReason: string | null;
  isAdmin?: boolean;
  profileId?: string | null;
}

export interface TokenValidationState {
  isLoading: boolean;
  result: TokenValidationResult | null;
  error: string | null;
}

let sessionTimeoutWarningShown = false; // Global flag to prevent duplicate warnings

// Cache TTL configuration for stale-while-revalidate pattern
const CACHE_TTL = 5 * 60 * 1000;         // 5 minutes - data is fresh
const STALE_TTL = 30 * 60 * 1000;        // 30 minutes - data is stale but usable
const REFRESH_THRESHOLD = 2 * 60 * 1000; // 2 minutes before expiry - trigger preemptive refresh

// Enhanced cache with refresh tracking
interface CacheEntry {
  result: TokenValidationResult | null;
  timestamp: number;
  isRefreshing: boolean;
}

const validationCache: {
  admin: CacheEntry | null;
  user: CacheEntry | null;
} = {
  admin: null,
  user: null
};

// Export function to clear validation cache (called after new login)
export const clearValidationCache = (context?: 'admin' | 'user') => {
  authLogger.info('Clearing validation cache', { context: context || 'all' });
  if (context) {
    validationCache[context] = null;
  } else {
    validationCache.admin = null;
    validationCache.user = null;
  }
};

// Export function to manually set validation cache (called after successful OTP verification)
export const setValidationCache = (context: 'admin' | 'user', result: TokenValidationResult) => {
  authLogger.cachePopulated(context, result.userEmail || 'unknown');
  validationCache[context] = { result, timestamp: Date.now(), isRefreshing: false };
};

// Read the current cached result for a context (no network call). Used by
// callers like the Admin toggle to seed the admin cache from the user cache.
export const getValidationCache = (context: 'admin' | 'user'): TokenValidationResult | null => {
  return validationCache[context]?.result ?? null;
};

export const useTokenValidation = (context?: 'admin' | 'user') => {
  const cacheKey = context || 'user';
  let cached = validationCache[cacheKey];

  // ADMIN-CONTEXT BOOTSTRAP:
  // Super admins authenticate via the unified `user_auth_token` (the user
  // session itself proves admin role server-side). When the AccessGate
  // mounts with context='admin', the dedicated `admin_auth_token` is
  // usually empty and the admin cache is empty too — which would falsely
  // resolve to "not authenticated" on the first render and trigger a
  // bounce to /admin/login. To prevent that, if we already have a valid
  // user-context cache that's marked admin, mirror it into the admin
  // cache synchronously so the first render of /admin is authenticated.
  if (context === 'admin' && (!cached || !cached.result?.isValid)) {
    const userCached = validationCache.user;
    if (userCached?.result?.isValid && userCached.result.isAdmin) {
      validationCache.admin = {
        result: { ...userCached.result },
        timestamp: userCached.timestamp,
        isRefreshing: false,
      };
      cached = validationCache.admin;
    }
  }

  // Quick check: if no tokens in localStorage, skip everything.
  // For admin context, ALSO accept a unified user token — admin role is
  // verified server-side during validate-session-cookie, and an authenticated
  // admin should not be forced to log in twice.
  const hasToken = context === 'admin'
    ? !!(localStorage.getItem('admin_auth_token') || localStorage.getItem('user_auth_token'))
    : context === 'user'
      ? !!localStorage.getItem('user_auth_token')
      : !!(localStorage.getItem('admin_auth_token') || localStorage.getItem('user_auth_token'));
  
  // Check cache state for initial render
  const cacheAge = cached ? Date.now() - cached.timestamp : Infinity;
  const hasAnyCachedData = cached?.result != null;
  const isCacheFresh = cacheAge < CACHE_TTL;
  const isCacheStale = cacheAge >= CACHE_TTL && cacheAge < STALE_TTL;
  
  const navigate = useNavigate();
  
  // If no token at all, immediately resolve as unauthenticated (no network call)
  const noTokenResult: TokenValidationResult = {
    isValid: false,
    userEmail: null,
    userName: null,
    paymentStatus: null,
    expiresAt: null,
    usesRemaining: null,
    failureReason: 'No token',
    isAdmin: false,
  };
  
  // STALE-WHILE-REVALIDATE: Never block if we have ANY cached data
  const [state, setState] = useState<TokenValidationState>({
    isLoading: hasToken ? !hasAnyCachedData : false,
    result: hasToken ? (cached?.result ?? null) : noTokenResult,
    error: null,
  });
  
  // Track stale/refreshing state
  const [isStale, setIsStale] = useState(isCacheStale);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Initialize session timeout hook
  const isAuthenticated = state.result?.isValid ?? false;
  useSessionTimeout(isAuthenticated);
  
  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastValidRef = useRef<boolean>(true);
  const sessionWarningIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Background refresh function - validates without blocking UI
  const validateInBackground = useCallback(async () => {
    const cached = validationCache[cacheKey];
    
    // Don't double-refresh
    if (cached?.isRefreshing) {
      authLogger.debug('[SWR] Background refresh already in progress');
      return;
    }
    
    // Mark as refreshing
    if (cached) {
      cached.isRefreshing = true;
    }
    setIsRefreshing(true);
    
    authLogger.debug('[SWR] Starting background token refresh', { context: cacheKey });
    
    try {
      const cookieData = await getAuthFromCookie(context);
      
      const newResult: TokenValidationResult = cookieData?.isValid ? {
        isValid: true,
        userEmail: cookieData.userEmail,
        userName: cookieData.userName,
        paymentStatus: cookieData.paymentStatus,
        expiresAt: cookieData.expiresAt ? new Date(cookieData.expiresAt) : null,
        usesRemaining: cookieData.usesRemaining,
        failureReason: null,
        isAdmin: cookieData.isAdmin || false,
      } : {
        isValid: false,
        userEmail: null,
        userName: null,
        paymentStatus: null,
        expiresAt: null,
        usesRemaining: null,
        failureReason: cookieData?.failureReason || 'Session expired',
        isAdmin: false,
      };

      // If we are downgrading a previously-valid session to invalid
      // (e.g. token revoked server-side or expired between SWR ticks),
      // surface a clear toast so the user understands the redirect
      // instead of being silently kicked back to /login.
      const wasValid = !!cached?.result?.isValid;
      if (wasValid && !newResult.isValid) {
        try {
          const reason = (newResult.failureReason || '').toLowerCase();
          const msg = reason.includes('invalid')
            ? 'Your session has expired. Please sign in again.'
            : 'Your session ended. Please sign in again.';
          toast.error(msg, { duration: 5000 });
        } catch {}
      }

      // Update cache with fresh data
      validationCache[cacheKey] = {
        result: newResult,
        timestamp: Date.now(),
        isRefreshing: false
      };

      // Silently update state (no loading flicker)
      setState(prev => ({
        ...prev,
        result: newResult,
        error: null,
      }));
      setIsStale(false);
      setIsRefreshing(false);

      authLogger.info('[SWR] Background refresh complete', {
        email: newResult.userEmail,
        wasValid: newResult.isValid
      });
      
      
    } catch (error) {
      authLogger.error('[SWR] Background refresh failed', { error: String(error) });
      if (cached) cached.isRefreshing = false;
      setIsRefreshing(false);
    }
  }, [context, cacheKey]);

  const extendSession = async () => {
    try {
      // Re-validate admin session from cookie
      const cookieData = await getAuthFromCookie('admin');
      
      if (cookieData?.isValid) {
        toast.success('Session extended successfully', {
          description: 'Your session has been refreshed'
        });
        sessionTimeoutWarningShown = false;
        
        const validResult = {
          isValid: true,
          userEmail: cookieData.userEmail,
          userName: cookieData.userName,
          paymentStatus: cookieData.paymentStatus,
          expiresAt: cookieData.expiresAt ? new Date(cookieData.expiresAt) : null,
          usesRemaining: cookieData.usesRemaining,
          failureReason: null,
          isAdmin: cookieData.isAdmin || false,
        };
        
        // Update cache
        validationCache[cacheKey] = { result: validResult, timestamp: Date.now(), isRefreshing: false };
        
        setState({
          isLoading: false,
          result: validResult,
          error: null,
        });
      } else {
        toast.error('Failed to extend session', {
          description: 'Please log in again'
        });
      }
    } catch (error) {
      console.error('Error extending session:', error);
      toast.error('Failed to extend session');
    }
  };

  const clearToken = async () => {
    // Clear authentication cookie
    await clearAuthCookie();
    
    // Clear the cache
    validationCache.admin = null;
    validationCache.user = null;
    
    setState({
      isLoading: false,
      result: {
        isValid: false,
        userEmail: null,
        userName: null,
        paymentStatus: null,
        expiresAt: null,
        usesRemaining: null,
        failureReason: 'Token cleared',
        isAdmin: false,
      },
      error: null,
    });
    
    // Redirect to unified login surface
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    const validateAccess = async () => {
      // FAST PATH: No token = not authenticated, skip network call entirely
      if (!hasToken) {
        setState({ isLoading: false, result: noTokenResult, error: null });
        return;
      }
      
      authLogger.validationStart(cacheKey as 'admin' | 'user');
      
      // Check localStorage state for debugging
      const adminToken = localStorage.getItem('admin_auth_token');
      const userToken = localStorage.getItem('user_auth_token');
      const adminEmail = localStorage.getItem('admin_auth_email');
      const userEmail = localStorage.getItem('user_auth_email');
      
      authLogger.debug('LocalStorage state', {
        hasAdminToken: !!adminToken,
        hasUserToken: !!userToken,
        adminTokenLength: adminToken?.length,
        userTokenLength: userToken?.length,
        adminEmail,
        userEmail
      });
      
      // Check cache state
      const cached = validationCache[cacheKey];
      const cacheAge = cached ? Date.now() - cached.timestamp : Infinity;
      const isCacheFresh = cacheAge < CACHE_TTL;
      const isCacheStale = cacheAge >= CACHE_TTL && cacheAge < STALE_TTL;
      const isNearExpiry = cached && cacheAge >= (CACHE_TTL - REFRESH_THRESHOLD);
      
      // CASE 1: Fresh cache - return immediately
      if (isCacheFresh && cached?.result) {
        authLogger.cacheHit(cacheKey as 'admin' | 'user', cached.result.userEmail, cacheAge);
        setState({
          isLoading: false,
          result: cached.result,
          error: null,
        });
        setIsStale(false);
        
        // Preemptive refresh if near expiry (within 2 minutes)
        if (isNearExpiry) {
          authLogger.debug('[SWR] Cache near expiry, preemptive refresh', { cacheAge });
          validateInBackground();
        }
        return;
      }
      
      // CASE 2: Stale cache - return stale data immediately, refresh in background
      if (isCacheStale && cached?.result) {
        authLogger.debug('[SWR] Using stale cache, refreshing in background', { 
          cacheAge,
          email: cached.result.userEmail 
        });
        
        setState({
          isLoading: false,  // DON'T block!
          result: cached.result,
          error: null,
        });
        setIsStale(true);
        
        // Trigger background refresh
        validateInBackground();
        return;
      }
      
      // CASE 3: No cache or cache too old - must validate (blocking)
      authLogger.cacheMiss(cacheKey as 'admin' | 'user', cached ? 'cache expired' : 'no cache');
      authLogger.serverValidationStart(cacheKey as 'admin' | 'user');
      
      // Set loading state for first-time validation
      setState(prev => ({ ...prev, isLoading: true }));
      
      // Always check for authentication (admin or user)
      const cookieData = await getAuthFromCookie(context);
      
      if (!cookieData || !cookieData.isValid) {
        authLogger.serverValidationFailed(cookieData?.failureReason || 'No session found');
        const invalidResult: TokenValidationResult = {
          isValid: false,
          userEmail: null,
          userName: null,
          paymentStatus: null,
          expiresAt: null,
          usesRemaining: null,
          failureReason: cookieData?.failureReason || 'No session found',
          isAdmin: false,
        };

        // If the server explicitly rejected the token (e.g. "Token invalid"),
        // purge the stale token from localStorage so we don't loop on a dead session.
        // We keep the token only when the failure is "No session found" (no token sent).
        const reason = (cookieData?.failureReason || '').toLowerCase();
        const tokenWasRejected = !!cookieData && reason.includes('invalid');
        if (tokenWasRejected) {
          try {
            localStorage.removeItem(`${cacheKey}_auth_token`);
            localStorage.removeItem(`${cacheKey}_auth_type`);
            localStorage.removeItem(`${cacheKey}_auth_email`);
            localStorage.removeItem(`${cacheKey}_remember_me`);
            authLogger.warn('Purged stale rejected token from localStorage', { bucket: cacheKey });
          } catch {}
        }

        // Cache the result
        validationCache[cacheKey] = { result: invalidResult, timestamp: Date.now(), isRefreshing: false };
        
        setState({
          isLoading: false,
          result: invalidResult,
          error: null,
        });
        setIsStale(false);
      } else {
        authLogger.serverValidationSuccess(cookieData.userEmail || 'unknown', cookieData.isAdmin || false);
        const validResult: TokenValidationResult = {
          isValid: true,
          userEmail: cookieData.userEmail,
          userName: cookieData.userName,
          paymentStatus: cookieData.paymentStatus,
          expiresAt: cookieData.expiresAt ? new Date(cookieData.expiresAt) : null,
          usesRemaining: cookieData.usesRemaining,
          failureReason: null,
          isAdmin: cookieData.isAdmin || false,
          profileId: cookieData.profileId || null,
        };
        
        // Cache profileId to localStorage for role fetching resilience
        if (cookieData.profileId) {
          localStorage.setItem('sirppi_profile_id', cookieData.profileId);
        }
        
        // Cache the result
        validationCache[cacheKey] = { result: validResult, timestamp: Date.now(), isRefreshing: false };
        
        setState({
          isLoading: false,
          result: validResult,
          error: null,
        });
        setIsStale(false);
      }
    };

    validateAccess();
  }, [context, cacheKey, validateInBackground]);

  // Phase 2: Session Health Monitoring
  useEffect(() => {
    const performHealthCheck = async () => {
      if (context === 'admin') {
        authLogger.debug('Performing session health check', { context });
        const cookieData = await getAuthFromCookie('admin');
        
        if (!cookieData || !cookieData.isValid) {
          authLogger.healthCheckFailed('admin', 'Session invalid or expired');
          
          toast.error('Session expired', {
            description: 'Your session has expired. Please log in again.',
            duration: 3000,
          });
          
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 1500);
          
          lastValidRef.current = false;
        } else {
          authLogger.healthCheckPassed('admin');
          lastValidRef.current = true;
        }
      }
    };

    // Initial health check after 5 minutes
    const initialTimeout = setTimeout(() => {
      performHealthCheck();
      
      // Then check every 5 minutes
      healthCheckIntervalRef.current = setInterval(performHealthCheck, 5 * 60 * 1000);
    }, 5 * 60 * 1000);

    // Cleanup on unmount
    return () => {
      clearTimeout(initialTimeout);
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
    };
  }, [context, navigate]);

  // Phase 3: Session Timeout Warning (5 minutes before expiry for admins)
  useEffect(() => {
    if (context !== 'admin' || !state.result?.expiresAt || !state.result.isValid) {
      return;
    }

    const checkSessionExpiry = () => {
      if (!state.result?.expiresAt || sessionTimeoutWarningShown) {
        return;
      }

      const expiresAt = new Date(state.result.expiresAt);
      const now = new Date();
      const timeUntilExpiry = expiresAt.getTime() - now.getTime();
      const fiveMinutesInMs = 5 * 60 * 1000;

      // Show warning if session expires in less than 5 minutes
      if (timeUntilExpiry > 0 && timeUntilExpiry <= fiveMinutesInMs && !sessionTimeoutWarningShown) {
        sessionTimeoutWarningShown = true;
        
        const minutesRemaining = Math.ceil(timeUntilExpiry / 60000);
        
        toast.warning('Session Expiring Soon', {
          description: `Your admin session will expire in ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}`,
          duration: 30000, // Show for 30 seconds
          action: {
            label: 'Extend Session',
            onClick: extendSession
          }
        });
      }
    };

    // Check immediately
    checkSessionExpiry();

    // Then check every minute
    sessionWarningIntervalRef.current = setInterval(checkSessionExpiry, 60 * 1000);

    return () => {
      if (sessionWarningIntervalRef.current) {
        clearInterval(sessionWarningIntervalRef.current);
      }
    };
  }, [context, state.result]);

  // Cross-tab logout sync: if another tab clears the auth token,
  // invalidate cache and redirect this tab to /login.
  useEffect(() => {
    const tokenKey = `${cacheKey}_auth_token`;
    const onStorage = (e: StorageEvent) => {
      if (e.key === tokenKey && e.newValue === null && e.oldValue) {
        authLogger.info('[CrossTab] Detected logout in another tab, syncing', { context: cacheKey });
        // Telemetry: cross-tab logout sync fired
        import('@/utils/telemetry').then(({ logEvent }) =>
          logEvent('auth.cross_tab_logout', { detail: { context: cacheKey } })
        ).catch(() => {});
        validationCache.admin = null;
        validationCache.user = null;
        setState({
          isLoading: false,
          result: noTokenResult,
          error: null,
        });
        navigate('/login', { replace: true });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, navigate]);

  return {
    ...state,
    clearToken,
    extendSession,
    isStale,
    isRefreshing,
  };
};
